/** The longest name kept for a person. */
export const MAX_PERSON_NAME = 120;

export interface NameSighting {
  source: 'saved' | 'handle';
  name: string;
}

// Format marks and the Hangul and Braille fillers behind "blank" names render as nothing, except
// the joiners that build emoji, flags and some scripts, which stay.
const HIDDEN = /[\p{Cf}\u115F\u1160\u2800\u3164\uFFA0]/gu;
const JOINER = /^[\u200C\u200D\u{E0020}-\u{E007F}]$/u;
const VISIBLE = /[^\s\u200C\u200D\u{E0020}-\u{E007F}]/u;

export const cleanName = (value: unknown) => {
  if (typeof value !== 'string') return '';
  const name = value
    .replace(/\p{Cc}/gu, ' ')
    .replace(HIDDEN, (mark) => (JOINER.test(mark) ? mark : ''))
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_PERSON_NAME);
  return VISIBLE.test(name) ? name : '';
};

/** Each name once per source, cleaned. Only blanks are left out: every other name is the owner's own. */
export function distinctNames(sightings: { source: NameSighting['source']; name: unknown }[]) {
  const seen = new Map<string, NameSighting>();
  for (const { source, name: value } of sightings) {
    const name = cleanName(value);
    if (name) seen.set(`${source}\u0000${name}`, { source, name });
  }
  return [...seen.values()];
}

/**
 * The names messages from one conversation carry for the person on the other side: what the
 * owner saved them as, and what their handle arrived with. A chat's own title counts as a
 * handle's name too, so a push name like "_ _" never hides it. Names say nothing about when they
 * were given, so the server dates each by when it first records it.
 */
export const namesInMessages = (messages: { metadata?: Record<string, unknown> }[]) =>
  distinctNames(
    messages.flatMap(({ metadata }) => [
      // One saved name, or each of several when a number was saved more than once.
      ...[metadata?.savedName].flat().map((name) => ({ source: 'saved' as const, name })),
      { source: 'handle' as const, name: metadata?.handleName },
      { source: 'handle' as const, name: metadata?.conversationName },
    ])
  );
